/**
 * WebSocket Client Library
 *
 * Handles WebSocket connection, reconnection, and message handling
 */

export type WebSocketEventType =
  | 'analysis:progress'
  | 'analysis:complete'
  | 'analysis:error'
  | 'alert:new'
  | 'alert:acknowledged'
  | 'indices:updated'
  | 'weather:updated'
  | 'connection:status'
  | 'subscription:confirmed'
  | 'subscription:removed'
  | 'authenticated'
  | 'pong';

export interface WebSocketMessage {
  type: WebSocketEventType;
  payload: unknown;
  timestamp: string;
  farmId?: string;
}

export type MessageHandler = (message: WebSocketMessage) => void;

export interface WebSocketClientOptions {
  url?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

const DEFAULT_OPTIONS: Required<Omit<WebSocketClientOptions, 'onConnect' | 'onDisconnect' | 'onError'>> = {
  url: import.meta.env.VITE_WS_URL || `ws://${window.location.hostname}:3001/ws`,
  reconnectInterval: 3000,
  maxReconnectAttempts: 10,
};

export class WebSocketClient {
  private socket: WebSocket | null = null;
  private options: WebSocketClientOptions;
  private handlers: Map<WebSocketEventType, Set<MessageHandler>> = new Map();
  private globalHandlers: Set<MessageHandler> = new Set();
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private isIntentionallyClosed = false;

  constructor(options: WebSocketClientOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isIntentionallyClosed = false;

    try {
      this.socket = new WebSocket(this.options.url!);

      this.socket.onopen = () => {
        console.log('[WebSocket] Connected');
        this.reconnectAttempts = 0;
        this.startPingInterval();
        this.options.onConnect?.();
      };

      this.socket.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      this.socket.onclose = () => {
        console.log('[WebSocket] Disconnected');
        this.stopPingInterval();
        this.options.onDisconnect?.();

        if (!this.isIntentionallyClosed) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        this.options.onError?.(error);
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.stopPingInterval();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  /**
   * Send message to server
   */
  send(type: string, payload?: Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot send message, not connected');
      return;
    }

    this.socket.send(
      JSON.stringify({
        type,
        ...(payload || {}),
        timestamp: new Date().toISOString(),
      })
    );
  }

  /**
   * Subscribe to a farm's updates
   */
  subscribeToFarm(farmId: string): void {
    this.send('subscribe:farm', { farmId });
  }

  /**
   * Unsubscribe from a farm's updates
   */
  unsubscribeFromFarm(farmId: string): void {
    this.send('unsubscribe:farm', { farmId });
  }

  /**
   * Authenticate with user ID
   */
  authenticate(userId: string): void {
    this.send('authenticate', { userId });
  }

  /**
   * Register handler for specific event type
   */
  on(eventType: WebSocketEventType, handler: MessageHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Register handler for all events
   */
  onAny(handler: MessageHandler): () => void {
    this.globalHandlers.add(handler);
    return () => {
      this.globalHandlers.delete(handler);
    };
  }

  /**
   * Remove handler for specific event type
   */
  off(eventType: WebSocketEventType, handler: MessageHandler): void {
    this.handlers.get(eventType)?.delete(handler);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state
   */
  getState(): 'connecting' | 'open' | 'closing' | 'closed' {
    switch (this.socket?.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'open';
      case WebSocket.CLOSING:
        return 'closing';
      default:
        return 'closed';
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    // Call global handlers
    this.globalHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        console.error('[WebSocket] Handler error:', error);
      }
    });

    // Call type-specific handlers
    const typeHandlers = this.handlers.get(message.type);
    if (typeHandlers) {
      typeHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error('[WebSocket] Handler error:', error);
        }
      });
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts!) {
      console.error('[WebSocket] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.options.reconnectInterval! * Math.min(this.reconnectAttempts, 5);

    console.log(
      `[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      this.send('ping');
    }, 30000); // Ping every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

// Singleton instance
let wsClient: WebSocketClient | null = null;

export function getWebSocketClient(options?: WebSocketClientOptions): WebSocketClient {
  if (!wsClient) {
    wsClient = new WebSocketClient(options);
  }
  return wsClient;
}

export default WebSocketClient;
